/**
 * 🔒 A list filtered by tag category, then by tag (piece 13; T1581, screen E of
 * the tag-categories exploration). Ticked tags inside one category mean either
 * of them; ticks across categories mean all of them; *Any* on a category's
 * header means any tag in it. The Deal list is the first list to carry it (the
 * owner's word of 2026-10-07); the shape is the list's, so another list adopts
 * it by passing its record type.
 */

/** The address parameter, named clear of the Item list's own category filter (screen E's cost). */
export const TAG_FILTER_PARAM = 'tagFilter';

/** The prefix marking a category's *Any* inside the parameter: `any:<categoryId>`. */
const ANY_PREFIX = 'any:';

/**
 * 🔒 The reserved field a saved view keeps its tag filter under, beside its conditions.
 * It names no column, so no condition row reads it and the New View Modal never draws it.
 */
export const TAG_FILTER_CRITERION = '@tagFilter';

/** What is ticked: tags by id, and the categories whose *Any* is chosen. */
export interface TagFilter {
  tagIds: readonly string[];
  anyCategoryIds: readonly string[];
}

export const EMPTY_TAG_FILTER: TagFilter = { tagIds: [], anyCategoryIds: [] };

function unique(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value !== ''))];
}

/** The parameter read: comma-separated tag ids, with `any:` before a category's id. Anything blank reads as no filter. */
export function parseTagFilter(text: string | null | undefined): TagFilter {
  const parts = (text ?? '').split(',').map((part) => part.trim()).filter(Boolean);
  return {
    tagIds: unique(parts.filter((part) => !part.startsWith(ANY_PREFIX))),
    anyCategoryIds: unique(parts.filter((part) => part.startsWith(ANY_PREFIX)).map((part) => part.slice(ANY_PREFIX.length))),
  };
}

/** The parameter written, the categories first and each half sorted, so one filter always reads as one address. */
export function formatTagFilter(filter: TagFilter): string {
  return [...unique(filter.anyCategoryIds).sort().map((id) => `${ANY_PREFIX}${id}`), ...unique(filter.tagIds).sort()].join(',');
}

export function isEmptyTagFilter(filter: TagFilter): boolean {
  return filter.tagIds.length === 0 && filter.anyCategoryIds.length === 0;
}

/** How many choices the menu's button counts: *Tags · 2*. */
export function tagFilterCount(filter: TagFilter): number {
  return filter.tagIds.length + filter.anyCategoryIds.length;
}

/** One tag as the filter reads it: its id, and the category it sits in while that category is in use, else null. */
export interface FilterableTag {
  id: string;
  categoryId: string | null;
}

/**
 * 🔒 The groups a record must meet, one per category touched (screen E): a
 * record matches when it carries at least one tag of every group. *Any* makes
 * its category's group every tag in it; ticked tags group by their category,
 * the uncategorized together. A tag or a category no longer offered, gone or out
 * of use, is read without, so a saved filter never empties a list on its own.
 */
export function tagFilterGroups(filter: TagFilter, tags: readonly FilterableTag[], categoriesInUse: readonly string[]): string[][] {
  const known = new Set(categoriesInUse);
  const anyIds = filter.anyCategoryIds.filter((id) => known.has(id));
  const groups = new Map<string, string[]>();
  for (const id of anyIds) groups.set(id, tags.filter((tag) => tag.categoryId === id).map((tag) => tag.id));
  const byId = new Map(tags.map((tag) => [tag.id, tag]));
  for (const id of filter.tagIds) {
    const tag = byId.get(id);
    if (!tag) continue;
    const key = tag.categoryId ?? '';
    if (anyIds.includes(key)) continue;
    groups.set(key, [...(groups.get(key) ?? []), tag.id]);
  }
  return [...groups.values()];
}

/** Ticking or unticking one tag. */
export function toggleFilterTag(filter: TagFilter, tagId: string): TagFilter {
  const on = filter.tagIds.includes(tagId);
  return { ...filter, tagIds: on ? filter.tagIds.filter((id) => id !== tagId) : [...filter.tagIds, tagId] };
}

/** Choosing or clearing a category's *Any*; choosing it clears that category's own ticks, which it already covers. */
export function toggleFilterCategory(filter: TagFilter, categoryId: string, tagIdsInCategory: readonly string[]): TagFilter {
  if (filter.anyCategoryIds.includes(categoryId)) return { ...filter, anyCategoryIds: filter.anyCategoryIds.filter((id) => id !== categoryId) };
  return { tagIds: filter.tagIds.filter((id) => !tagIdsInCategory.includes(id)), anyCategoryIds: [...filter.anyCategoryIds, categoryId] };
}
